import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import PromotionsNearby from '../components/PromotionsNearby';
import SuggestInput from '../components/SuggestInput';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  MagnifyingGlass, MapPin, User, ShieldCheck, WarningCircle,
  Lightning, ArrowRight, ArrowLeft, CheckCircle, UsersThree, Sparkle, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PENDING_REQUEST_KEY } from '../components/PendingRequestSubmitter';

const RequestPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Reference data
  const [allNeeds, setAllNeeds] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [coveredProvinces, setCoveredProvinces] = useState([]);
  const [comuneCheck, setComuneCheck] = useState(null);
  const [addressCheck, setAddressCheck] = useState(null);
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [platformCfg, setPlatformCfg] = useState({});

  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedNeeds, setSelectedNeeds] = useState([]); // need_id
  const [selectedProfessions, setSelectedProfessions] = useState([]); // profession codes

  const [form, setForm] = useState({
    region: '',
    province: '',
    comune: '',
    street: '',
    cap: '',
    is_patient: true,
    patient_phone: '',
    family_phone: '',
    urgency: 'medium',
    is_urgent: false,
    urgent_surcharge_eur: 0,
    budget: '',
    budget_type: 'from', // 'a partire da'
    start_date: '',
    end_date: '',
    requester_name: '',
    patient_name: '',
    family_member_name: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    health_conditions: '',
    privacy_consent: false,
  });

  // L'email di contatto per gli utenti loggati è sempre quella dell'account
  useEffect(() => {
    if (user?.email) setForm((f) => (f.contact_email === user.email ? f : { ...f, contact_email: user.email }));
  }, [user]);

  // Precompila i dati di contatto dall'account (restano modificabili)
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    api.get('/api/me/account').then((r) => {
      if (!active) return;
      const a = r.data || {};
      setForm((f) => ({
        ...f,
        requester_name: f.requester_name || a.name || '',
        patient_name: f.patient_name || a.name || '',
        contact_phone: f.contact_phone || a.phone || '',
        region: f.region || a.region || '',
      }));
    }).catch(() => {});
    return () => { active = false; };
  }, [isAuthenticated]);


  // Pro suggestions (Fase D hybrid: AI-suggested + client add manual)
  const [suggestedPros, setSuggestedPros] = useState([]);
  const [selectedPros, setSelectedPros] = useState([]); // professional_id[]
  const [proSearch, setProSearch] = useState([]);
  const [proSearchQ, setProSearchQ] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showAllPros, setShowAllPros] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [n, r, cfg] = await Promise.all([
          api.get('/api/needs'),
          api.get('/api/regions'),
          api.get('/api/platform-config'),
        ]);
        setAllNeeds(n.data);
        setRegions(r.data);
        setPlatformCfg(cfg.data);
        setForm((f) => ({ ...f, urgent_surcharge_eur: cfg.data.urgent_surcharge_default_eur || 0 }));
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    if (form.region) {
      api.get(`/api/geo/covered-provinces?region=${encodeURIComponent(form.region)}`)
        .then((r) => setCoveredProvinces(r.data.provinces || []))
        .catch(() => setCoveredProvinces([]));
      api.get(`/api/zones?region=${encodeURIComponent(form.region)}`)
        .then((r) => setProvinces(r.data)).catch(() => {});
    } else { setProvinces([]); }
  }, [form.region]);

  const fetchComuni = useCallback(async (q) => {
    const r = await api.get(`/api/geo/comuni?province=${encodeURIComponent(form.province)}&q=${encodeURIComponent(q)}&limit=8`);
    return r.data.items || [];
  }, [form.province]);

  const pickComune = (c) => {
    setForm((f) => ({ ...f, comune: c.comune, street: '', cap: c.caps.length === 1 ? c.caps[0] : '' }));
    setComuneCheck({ valid: true, comune: c.comune, provincia: c.provincia, caps: c.caps });
    setAddressCheck(null);
  };

  const fetchStreets = useCallback(async (q) => {
    const r = await api.get(`/api/geo/streets?comune=${encodeURIComponent(form.comune)}&q=${encodeURIComponent(q)}`);
    return r.data.items || [];
  }, [form.comune]);

  const pickStreet = (x) => {
    setForm((f) => ({ ...f, street: x.street, cap: x.cap || f.cap, latitude: x.latitude, longitude: x.longitude }));
    setAddressCheck({ valid: true, formatted: x.formatted, cap: x.cap });
  };

  const checkComune = async () => {
    if (!form.comune || !form.province) { toast.error('Scegli la provincia e scrivi il comune'); return; }
    try {
      const r = await api.get(`/api/geo/comuni/validate?comune=${encodeURIComponent(form.comune)}&province=${encodeURIComponent(form.province)}`);
      setComuneCheck(r.data);
      if (r.data.valid) {
        setForm((f) => ({ ...f, comune: r.data.comune, cap: (r.data.caps || []).length === 1 ? r.data.caps[0] : f.cap }));
        toast.success('Comune confermato');
      } else {
        toast.error(r.data.message || 'Comune non valido');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica comune');
    }
  };

  const checkAddress = async () => {
    if (!form.street || !comuneCheck?.valid) { toast.error('Verifica prima il comune e inserisci la via'); return; }
    setCheckingAddress(true);
    try {
      const r = await api.post('/api/geo/validate-address', {
        street: form.street, comune: form.comune, province: form.province, region: form.region,
      });
      setAddressCheck(r.data);
      if (r.data.valid) {
        setForm((f) => ({
          ...f,
          cap: r.data.cap && (comuneCheck.caps || []).includes(r.data.cap) ? r.data.cap : f.cap,
          latitude: r.data.latitude, longitude: r.data.longitude,
        }));
        toast.success(`Indirizzo confermato${r.data.cap ? ` · CAP ${r.data.cap}` : ''}`);
      } else {
        toast.warning('Indirizzo non confermato: controlla la via o scegli il CAP dall\u2019elenco');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica indirizzo');
    } finally { setCheckingAddress(false); }
  };

  // Group needs by context_code (macro-category); fall back to "Altro"
  const categories = useMemo(() => {
    const map = {};
    allNeeds.forEach((n) => {
      const key = n.context_code || 'altro';
      const label = n.category || n.context_label || 'Altro';
      if (!map[key]) map[key] = { code: key, label, items: [] };
      map[key].items.push(n);
    });
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [allNeeds]);

  const currentCategory = categories.find((c) => c.code === selectedCategory);

  const toggleNeed = (id) => setSelectedNeeds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleProfession = (code) => setSelectedProfessions((p) => p.includes(code) ? p.filter((c) => c !== code) : [...p, code]);
  const togglePro = (id) => setSelectedPros((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length < 5 ? [...prev, id] : (toast.warning('Massimo 5 professionisti'), prev)));

  const uniqueProfessions = useMemo(() => {
    const codes = new Set();
    (selectedNeeds
      .map((id) => allNeeds.find((n) => n.need_id === id))
      .filter(Boolean)
    ).forEach((n) => (n.profession_codes || []).forEach((c) => codes.add(c)));
    return [...codes];
  }, [selectedNeeds, allNeeds]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const r = await api.post('/api/requests/suggest-professionals', {
        profession_codes: selectedProfessions.length ? selectedProfessions : uniqueProfessions,
        need_ids: selectedNeeds,
        region: form.region || null,
        province: form.province || null,
        comune: form.comune || null,
        cap: form.cap || null,
        budget: form.budget ? parseFloat(form.budget) : null,
        limit: 5,
      });
      setSuggestedPros(r.data.professionals || []);
      setSelectedPros((r.data.professionals || []).map((p) => p.professional_id));
    } catch (e) { /* ignore */ }
  }, [selectedProfessions, uniqueProfessions, selectedNeeds, form.region, form.province, form.comune, form.cap]);

  const searchMorePros = async () => {
    if (!proSearchQ.trim()) return;
    try {
      const r = await api.get(`/api/professionals?location=${encodeURIComponent(proSearchQ)}`);
      setProSearch(r.data.slice(0, 20));
    } catch { setProSearch([]); }
  };

  // Auto-load suggestions when arriving at step 4
  useEffect(() => { if (step === 4) fetchSuggestions(); }, [step, fetchSuggestions]);

  const goNext = () => {
    if (step === 1 && !selectedCategory) return toast.error('Scegli una categoria');
    if (step === 2 && selectedNeeds.length === 0) return toast.error('Seleziona almeno un bisogno');
    if (step === 3 && (!form.region || !form.province || !form.comune || !form.cap)) {
      return toast.error('Regione, provincia, comune e CAP sono obbligatori');
    }
    if (step === 3 && !form.is_patient && (!form.patient_name || !form.patient_phone || !form.family_phone)) {
      return toast.error('Indica nome e telefono del paziente e il telefono di un familiare');
    }
    if (step === 3 && (!form.contact_phone || !form.contact_email)) {
      return toast.error('Nome paziente, telefono ed email sono obbligatori');
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (selectedNeeds.length === 0) return toast.error('Seleziona almeno un bisogno');
    if (!form.province || !form.comune) return toast.error('Provincia e comune sono obbligatori');
    if (!form.privacy_consent) return toast.error('Devi accettare l\'informativa privacy');
    // Se ci sono suggerimenti, almeno uno deve restare selezionato; se la zona non ha
    // professionisti disponibili la richiesta parte comunque e la smisteremo noi.
    if (suggestedPros.length > 0 && selectedPros.length === 0) {
      return toast.error('Seleziona almeno un professionista tra quelli suggeriti');
    }

    const payload = {
      ...form,
      needs: selectedNeeds,
      need_category: selectedCategory,
      profession_codes: selectedProfessions.length ? selectedProfessions : uniqueProfessions,
      selected_professional_ids: selectedPros,
      budget: form.budget ? parseFloat(form.budget) : null,
      budget_type: form.budget_type,
      urgent_surcharge_eur: form.is_urgent ? Number(form.urgent_surcharge_eur || 0) : 0,
    };

    // Utente non registrato: conserviamo la richiesta e la inviamo dopo la registrazione
    if (!isAuthenticated) {
      localStorage.setItem(PENDING_REQUEST_KEY, JSON.stringify({ payload, saved_at: new Date().toISOString() }));
      toast.success('Richiesta presa in carico: registrati per inviarla e vedere gli esiti');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/requests', payload);
      setResult(res.data);
      toast.success('Richiesta inviata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio');
    } finally { setLoading(false); }
  };

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background">
        {!isAuthenticated && <Navbar />}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="neumorphism rounded-2xl p-8" data-testid="request-result">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={40} className="text-primary" weight="duotone" />
              </div>
              <h1 className="font-heading font-semibold text-3xl mb-2">Richiesta Inviata!</h1>
              <p className="text-foreground/70">
                {result.matched_professionals?.length > 0
                  ? `${result.matched_professionals.length} professionist${result.matched_professionals.length > 1 ? 'i sono stati notificati' : 'a è stato notificato'} e ti contatteranno a breve.`
                  : 'Nessun professionista corrispondente. La nostra sede centrale ti ricontatterà.'}
              </p>
              {result.request?.is_urgent && (
                <Badge className="mt-3 bg-red-100 text-red-800">
                  <Lightning size={12} className="mr-1" /> URGENTE — sovrapprezzo €{result.request.urgent_surcharge_eur}
                </Badge>
              )}
              {!result.zone_active && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800">
                  <WarningCircle size={16} className="inline mr-1" /> La tua zona non è ancora attiva. Contatteremo la sede centrale.
                </div>
              )}
            </div>

            {result.matched_professionals?.length > 0 && (
              <div className="space-y-3 mb-6" data-testid="matched-pros-list">
                {(showAllPros ? result.matched_professionals : result.matched_professionals.slice(0, 5)).map((p) => (
                  <div key={p.professional_id} className="border border-border rounded-xl p-4 flex items-center justify-between" data-testid={`matched-${p.professional_id}`}>
                    <div>
                      <p className="font-medium">{p.name} {p.is_premium && <Badge className="ml-1 bg-accent text-white text-xs">PREMIUM</Badge>}</p>
                      <p className="text-sm text-foreground/70">{p.location} • {p.hourly_rate}€/ora • ★ {p.rating}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/professional/${p.professional_id}`)}>Vedi Profilo</Button>
                  </div>
                ))}
                {result.matched_professionals.length > 5 && !showAllPros && (
                  <div className="text-center pt-2">
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setShowAllPros(true)}
                      data-testid="show-all-pros-btn"
                    >
                      Vedi tutti i professionisti ({result.matched_professionals.length})
                    </Button>
                  </div>
                )}
                {showAllPros && result.matched_professionals.length > 5 && (
                  <div className="text-center pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => setShowAllPros(false)}
                      data-testid="collapse-pros-btn"
                    >
                      Mostra solo i primi 5
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="rounded-full" onClick={() => window.location.reload()}>Nuova Richiesta</Button>
              <Button className="rounded-full" onClick={() => navigate('/dashboard/richieste')} data-testid="go-dashboard-btn">Vai alle mie richieste</Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const emergencyNotice = platformCfg.emergency_notice_112;
  const surchargeMin = platformCfg.urgent_surcharge_min_eur || 0;

  return (
    <div className="min-h-screen bg-background">
      {!isAuthenticated && <Navbar />}
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="request-page">
        <div className="text-center mb-10">
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl mb-3">Richiedi Assistenza</h1>
          <p className="text-foreground/70">Compila 4 semplici passaggi e riceverai proposte dai professionisti giusti.</p>
        </div>

        {/* Emergency banner */}
        {emergencyNotice && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="emergency-notice">
            <WarningCircle size={16} className="inline mr-1" /> {emergencyNotice}
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="request-stepper">
          {['Categoria', 'Bisogni', 'Dettagli', 'Professionisti'].map((label, i) => {
            const n = i + 1; const active = step === n; const done = step > n;
            return (
              <React.Fragment key={n}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-sky-100 text-sky-800' : 'bg-muted text-foreground/60'}`}>
                  {done ? <CheckCircle size={16} weight="fill" /> : <span className="font-semibold text-xs">{n}</span>}
                  <span className="text-xs font-medium">{label}</span>
                </div>
                {i < 3 && <div className="flex-1 h-px bg-border" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1: Category */}
        {step === 1 && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step-category">
            <h2 className="font-heading font-semibold text-2xl mb-2">Che tipo di supporto ti serve?</h2>
            <p className="text-foreground/70 mb-6">Scegli la categoria più adatta: accanto a ogni voce trovi quanti servizi contiene.</p>
            <div className="max-w-xl">
              <Label className="text-xs">Categoria *</Label>
              <Select value={selectedCategory || ''} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="category-select">
                  <SelectValue placeholder="Seleziona una categoria" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {categories.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`cat-${c.code}`}>
                      {c.label} — {c.items.length} {c.items.length === 1 ? 'servizio' : 'servizi'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentCategory && (
                <p className="text-xs text-foreground/60 mt-2" data-testid="category-count">
                  {currentCategory.items.length} {currentCategory.items.length === 1 ? 'servizio disponibile' : 'servizi disponibili'} in «{currentCategory.label}»
                </p>
              )}
            </div>
          </Card>
        )}

        {/* STEP 2: Needs multi-select */}
        {step === 2 && currentCategory && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step-needs">
            <h2 className="font-heading font-semibold text-2xl mb-2">Servizi — {currentCategory.label}</h2>
            <p className="text-foreground/70 mb-6">Scegli dal menu i servizi che ti servono, uno alla volta. Selezionati: <strong>{selectedNeeds.length}</strong></p>
            <div className="max-w-xl">
              <Label className="text-xs">Servizio *</Label>
              <Select value="" onValueChange={toggleNeed}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="need-select">
                  <SelectValue placeholder="Aggiungi un servizio" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="max-h-[320px]">
                  {currentCategory.items.map((n) => (
                    <SelectItem key={n.need_id} value={n.need_id} data-testid={`need-${n.need_id}`}>
                      {selectedNeeds.includes(n.need_id) ? '✓ ' : ''}{n.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedNeeds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="selected-needs">
                {selectedNeeds.map((id) => {
                  const n = currentCategory.items.find((x) => x.need_id === id);
                  if (!n) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleNeed(id)}
                      className="px-3 py-1.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 transition-colors flex items-center gap-1.5"
                      data-testid={`selected-need-${id}`}
                    >
                      {n.label}
                      <X size={12} />
                    </button>
                  );
                })}
              </div>
            )}

            {uniqueProfessions.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border/60">
                <div className="text-sm text-foreground/70 mb-2">
                  Professioni suggerite in base ai bisogni: (puoi affinare)
                </div>
                <div className="flex flex-wrap gap-2">
                  {uniqueProfessions.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleProfession(code)}
                      className={`px-3 py-1 rounded-full text-xs border ${selectedProfessions.includes(code) || (!selectedProfessions.length) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
                      data-testid={`prof-chip-${code}`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* STEP 3: Details, location, urgent */}
        {step === 3 && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step-details">
            <h2 className="font-heading font-semibold text-2xl mb-6">Dettagli richiesta</h2>

            {/* Location */}
            <div className="mb-6">
              <h3 className="font-semibold text-sm text-foreground/60 uppercase tracking-wider mb-3 flex items-center gap-1">
                <MapPin size={14} /> Dove serve
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Regione *</Label>
                  <Select value={form.region} onValueChange={(v) => { setForm({ ...form, region: v, province: '', comune: '', street: '', cap: '' }); setComuneCheck(null); setAddressCheck(null); }}>
                    <SelectTrigger data-testid="region-select"><SelectValue placeholder="Scegli regione" /></SelectTrigger>
                    <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provincia coperta *</Label>
                  <Select value={form.province} onValueChange={(v) => { setForm({ ...form, province: v, comune: '', street: '', cap: '' }); setComuneCheck(null); setAddressCheck(null); }} disabled={!form.region}>
                    <SelectTrigger data-testid="province-select"><SelectValue placeholder={coveredProvinces.length ? 'Scegli provincia' : 'Nessuna provincia coperta'} /></SelectTrigger>
                    <SelectContent>{coveredProvinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  {form.region && coveredProvinces.length === 0 && (
                    <p className="text-xs text-amber-800" data-testid="no-covered-provinces">
                      In questa regione non ci sono ancora professionisti o Centri attivi.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Comune *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SuggestInput
                        value={form.comune}
                        onChange={(v) => { setForm({ ...form, comune: v, street: '', cap: '' }); setComuneCheck(null); setAddressCheck(null); }}
                        onPick={pickComune}
                        fetcher={fetchComuni}
                        renderItem={(c) => `${c.comune} (${c.sigla}) — ${c.caps.length} CAP`}
                        placeholder="Es. Milano"
                        disabled={!form.province}
                        testId="comune-input"
                        emptyHint="Nessun comune con questo nome nella provincia scelta"
                      />
                    </div>
                    <Button type="button" variant="outline" className="rounded-full" onClick={checkComune} data-testid="check-comune-btn">Verifica</Button>
                  </div>
                  {comuneCheck && (
                    <p className={`text-xs ${comuneCheck.valid ? 'text-emerald-700' : 'text-red-700'}`} data-testid="comune-check-result">
                      {comuneCheck.valid ? `Comune confermato (${comuneCheck.caps?.length || 0} CAP)` : comuneCheck.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Via dove svolgere la prestazione *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SuggestInput
                        value={form.street}
                        onChange={(v) => { setForm({ ...form, street: v }); setAddressCheck(null); }}
                        onPick={pickStreet}
                        fetcher={fetchStreets}
                        renderItem={(x) => x.formatted}
                        placeholder="Es. Corso Buenos Aires 10"
                        disabled={!comuneCheck?.valid}
                        testId="street-input"
                        minChars={3}
                        debounceMs={700}
                        emptyHint="Nessuna via trovata: controlla come l'hai scritta"
                      />
                    </div>
                    <Button type="button" variant="outline" className="rounded-full" onClick={checkAddress} disabled={checkingAddress} data-testid="check-address-btn">
                      {checkingAddress ? 'Verifica…' : 'Verifica via'}
                    </Button>
                  </div>
                  {addressCheck && (
                    <p className={`text-xs ${addressCheck.valid ? 'text-emerald-700' : 'text-amber-800'}`} data-testid="address-check-result">
                      {addressCheck.valid ? `Indirizzo confermato: ${addressCheck.formatted}` : 'Indirizzo non confermato: controlla la via o scegli il CAP manualmente'}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>CAP *</Label>
                  <Select value={form.cap} onValueChange={(v) => setForm({ ...form, cap: v })} disabled={!comuneCheck?.valid}>
                    <SelectTrigger data-testid="cap-select"><SelectValue placeholder="CAP" /></SelectTrigger>
                    <SelectContent>
                      {(comuneCheck?.caps || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-foreground/60">Rilevato in automatico dalla via; serve a smistare la richiesta.</p>
                </div>
              </div>
            </div>

            {/* Urgent toggle */}
            <div className="mb-6 p-4 rounded-xl bg-amber-50/60 border border-amber-200">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Lightning className="text-amber-600" size={20} weight="fill" />
                  <div>
                    <div className="font-semibold">Richiesta urgente</div>
                    <div className="text-xs text-foreground/60">Priorità in coda + notifica immediata ai professionisti</div>
                  </div>
                </div>
                <Switch
                  checked={form.is_urgent}
                  onCheckedChange={(v) => setForm({ ...form, is_urgent: v, urgent_surcharge_eur: v ? (platformCfg.urgent_surcharge_default_eur || surchargeMin) : 0 })}
                  data-testid="urgent-switch"
                />
              </div>
              {form.is_urgent && (
                <div className="mt-3">
                  <Label className="text-xs">Sovrapprezzo urgente (min €{surchargeMin})</Label>
                  <Input
                    type="number"
                    min={surchargeMin}
                    step="1"
                    value={form.urgent_surcharge_eur}
                    onChange={(e) => setForm({ ...form, urgent_surcharge_eur: Number(e.target.value) })}
                    data-testid="urgent-surcharge-input"
                  />
                </div>
              )}
            </div>

            {/* Patient / Contact */}
            <div className="mb-6">
              <h3 className="font-semibold text-sm text-foreground/60 uppercase tracking-wider mb-3 flex items-center gap-1">
                <User size={14} /> Contatti
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Chi fa la richiesta *</Label>
                  <Input value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} data-testid="requester-name-input" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Sei tu il paziente? *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button" variant={form.is_patient ? 'default' : 'outline'} className="rounded-full"
                      onClick={() => setForm({ ...form, is_patient: true, patient_name: form.requester_name || user?.name || '' })}
                      data-testid="is-patient-yes"
                    >Sì, sono io</Button>
                    <Button
                      type="button" variant={!form.is_patient ? 'default' : 'outline'} className="rounded-full"
                      onClick={() => setForm({ ...form, is_patient: false, patient_name: '' })}
                      data-testid="is-patient-no"
                    >No, è un'altra persona</Button>
                  </div>
                </div>
                {!form.is_patient && (
                  <>
                    <div className="space-y-2">
                      <Label>Nome del paziente *</Label>
                      <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="patient-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono del paziente *</Label>
                      <Input type="tel" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} data-testid="patient-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono di un familiare *</Label>
                      <Input type="tel" value={form.family_phone} onChange={(e) => setForm({ ...form, family_phone: e.target.value })} data-testid="family-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Familiare di riferimento</Label>
                      <Input value={form.family_member_name} onChange={(e) => setForm({ ...form, family_member_name: e.target.value })} data-testid="family-member-input" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Telefono contatto *</Label>
                  <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} required data-testid="phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email contatto *</Label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    disabled={isAuthenticated}
                    required
                    placeholder="mario.rossi@email.it"
                    data-testid="email-input"
                  />
                  <p className="text-xs text-foreground/60">
                    {isAuthenticated
                      ? 'Email del tuo account: le comunicazioni arriveranno qui.'
                      : 'Usa la stessa email con cui completerai la registrazione: la useremo per inviarti gli esiti.'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Budget indicativo</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground/70 shrink-0">A partire da</span>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground/60">€</span>
                      <Input
                        type="number"
                        min="0"
                        className="pl-7"
                        value={form.budget}
                        onChange={(e) => setForm({ ...form, budget: e.target.value })}
                        placeholder="es. 25"
                        data-testid="budget-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-foreground/60">
                    Facoltativo: ti proporremo prima i professionisti con tariffa più vicina al tuo budget.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <Label>Descrizione</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="description-input" placeholder="Contesto, condizioni del paziente, aspettative…" />
            </div>

            <div className="space-y-2 mb-6">
              <Label>Patologie o condizioni particolari <span className="text-foreground/50 text-xs">(opzionale)</span></Label>
              <Textarea
                rows={3} value={form.health_conditions}
                onChange={(e) => setForm({ ...form, health_conditions: e.target.value })}
                data-testid="health-conditions-input"
                placeholder="Es. diabete, Alzheimer iniziale, allettamento, allergie, terapie in corso…"
              />
              <p className="text-xs text-foreground/60">
                Queste informazioni sono visibili solo ai professionisti a cui invii la richiesta e li
                aiutano a valutare correttamente l&apos;intervento.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 bg-muted/40 rounded-xl">
              <Checkbox checked={form.privacy_consent} onCheckedChange={(v) => setForm({ ...form, privacy_consent: v === true })} className="mt-1" data-testid="privacy-checkbox" />
              <div className="text-xs text-foreground/80">
                Accetto l&apos;<a href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline">informativa privacy</a> e autorizzo il trattamento dei dati sanitari.
              </div>
            </label>
          </Card>
        )}

        {/* STEP 4: Multi-select Professionals (AI + manual) */}
        {step === 4 && (
          <Card className="neumorphism rounded-2xl p-8" data-testid="step-pros">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle className="text-primary" weight="fill" size={20} />
              <h2 className="font-heading font-semibold text-2xl">Professionisti suggeriti</h2>
            </div>
            <p className="text-foreground/70 mb-6">Selezioniamo automaticamente i migliori 5 per i tuoi bisogni. Puoi scartarne alcuni e aggiungerne altri.</p>

            <div className="mb-6">
              <PromotionsNearby
                region={form.region}
                province={form.province}
                comune={form.comune}
                title="Promozioni attive nella tua zona"
                compact
              />
            </div>

            {suggestedPros.length === 0 && (
              <p className="text-sm text-foreground/60 mb-4" data-testid="no-suggestions-note">
                Nessun professionista disponibile in automatico per questa zona: puoi aggiungerne manualmente qui sotto
                oppure inviare la richiesta e ci penseremo noi a trovarti i professionisti migliori.
              </p>
            )}

            <div className="space-y-3 mb-6" data-testid="suggested-list">
              {suggestedPros.map((p) => {
                const active = selectedPros.includes(p.professional_id);
                return (
                  <div
                    key={p.professional_id}
                    className={`p-4 rounded-xl border flex items-center justify-between gap-3 ${active ? 'border-primary bg-primary/5' : 'border-border'}`}
                    data-testid={`sug-pro-${p.professional_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox checked={active} onCheckedChange={() => togglePro(p.professional_id)} />
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {p.name}
                          {p.is_center && <Badge className="bg-primary/10 text-primary text-xs">CENTRO</Badge>}
                          {p.is_premium && <Badge className="bg-accent text-white text-xs">PREMIUM</Badge>}
                        </div>
                        <div className="text-xs text-foreground/70">
                          {p.is_center
                            ? ['Centro di assistenza', ...(p.profession_codes || []).map((c) => c.replace(/_/g, ' '))].join(' · ')
                            : (p.profession_code || p.specializations?.[0])}
                          {' • '}{p.location || p.city}{p.hourly_rate ? ` • €${p.hourly_rate}/h` : ''} • ★ {p.rating || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Manual search */}
            <div className="pt-6 border-t border-border/60">
              <div className="flex items-center gap-2 mb-3">
                <UsersThree size={20} className="text-foreground/60" weight="duotone" />
                <h3 className="font-semibold">Aggiungi altri manualmente</h3>
              </div>
              <div className="flex gap-2 mb-3">
                <Input
                  value={proSearchQ}
                  onChange={(e) => setProSearchQ(e.target.value)}
                  placeholder="Cerca per città o regione"
                  data-testid="pro-search-input"
                />
                <Button type="button" variant="outline" onClick={searchMorePros} data-testid="pro-search-btn">
                  <MagnifyingGlass size={16} className="mr-1" /> Cerca
                </Button>
              </div>
              {proSearch.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {proSearch.map((p) => {
                    const active = selectedPros.includes(p.professional_id);
                    const alreadySuggested = suggestedPros.some((s) => s.professional_id === p.professional_id);
                    if (alreadySuggested) return null;
                    return (
                      <div key={p.professional_id} className={`p-3 rounded-lg border flex items-center justify-between ${active ? 'border-primary bg-primary/5' : 'border-border'}`}>
                        <div className="text-sm">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-foreground/60">{p.location || p.city} • €{p.hourly_rate}/h</div>
                        </div>
                        <Button size="sm" variant={active ? 'default' : 'outline'} onClick={() => togglePro(p.professional_id)} data-testid={`add-pro-${p.professional_id}`}>
                          {active ? 'Rimuovi' : 'Aggiungi'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 p-3 bg-muted/40 rounded-xl text-sm">
              Hai selezionato <strong>{selectedPros.length}</strong> professionist{selectedPros.length === 1 ? 'a' : 'i'} su 5.
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <Button variant="outline" onClick={goBack} className="rounded-full" data-testid="wizard-back-btn">
              <ArrowLeft size={16} className="mr-1" /> Indietro
            </Button>
          ) : <div />}
          {step < 4 ? (
            <Button onClick={goNext} className="rounded-full" data-testid="wizard-next-btn">
              Continua <ArrowRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="rounded-full" data-testid="submit-request-btn">
              {loading ? 'Invio…' : 'Invia richiesta'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestPage;
